define([
	"dojo/_base/declare",
	"mathjs",
	"csi/_base/underscore",
	"csi/locale",
	"csi/heading/_base",
	"./graphs/SlicerDataAccess"
], function(declare,  { evaluate }, _, locale, csiHeading, SlicerDataAccess) {

	var KEY_SEPARATOR = "\x1F";

	var FUNCTIONS = {
		AVG: function(items) {
			var n = items.length;
			var x = 0;
			for (var i = 0; i < n; ++i) {
				x += items[i];
			}
			return x / n;
		},

		WEIGHT_AVG: function(items, weights) {
			var n = items.length;
			var x = 0;
			var w = 0;
			for (var i = 0; i < n; ++i) {
				x += items[i] * weights[i];
				w += weights[i];
			}
			return w ? x / w : 0;
		},


		COUNT: function(items) {
			return items.length;
		},

		MIN: function(items) {
			var n = items.length;
			var x = items[0];
			for (var i = 1; i < n; ++i) {
				x = Math.min(x, items[i]);
			}
			return x;
		},

		MAX: function(items) {
			var n = items.length;
			var x = items[0];
			for (var i = 1; i < n; ++i) {
				x = Math.max(x, items[i]);
			}
			return x;
		},

		SUM: function(items) {
			var n = items.length;
			var x = 0;
			for (var i = 0; i < n; ++i) {
				x += (items[i] || 0);
			}
			return x;
		}
	};

	var MetricValues = declare(null, {
		item: null,
		values: null,

		constructor: function(/*Object*/item) {
			this.item = item;
			this.values = [];
		}
	});

	/**
	 * Simplified scope for mathjs evaluation
	 * oldStyle scope, which include object with very deep references: they include ref to SlicerDataAccessAdapter and slicerWidget
	 * which mathjs is unable to parse
	 * @param {DynamicScope} scope - oldStyle scope
	 * @param {object} totals - represent all expression variables values
	 * @returns object
	 */
	function expressionScope(scope, totals) {
		const functions = {
			TOTAL: function(mKey) {
				return scope.TOTAL(mKey);
			},
			COUNT_UNIQUE: function(clsKey) {
				return scope.COUNT_UNIQUE(clsKey);
			}
		}
		_.extend(functions, totals);
		return functions;
	}


	var DynamicScope = declare(null, {
		// SlicerDataAccessAdapter
		slicerDataAccessAdapter: null,

		//KeyValues
		keyValuesForDynamic: null,

		constructor: function(/*SlicerDataAccessAdapter*/slicerDataAccessAdapter, /*Object*/props, /*KeyValues*/keyValuesForDynamic) {
			this.slicerDataAccessAdapter = slicerDataAccessAdapter;
			this.keyValuesForDynamic = keyValuesForDynamic;
			_.extend(this, props);
		},

		TOTAL: function(/*String*/metricKey) {
			// summary: get the total of given metric for classifier values given by 'this.keyValuesForDynamic'
			var metricValues = this.slicerDataAccessAdapter.getMetricValues(this.keyValuesForDynamic)[metricKey];
			if (!metricValues)
				return null;
			return FUNCTIONS.SUM(metricValues.values);
		},

		COUNT_UNIQUE: function(/*String*/classifierKey) {
			// summary: return the number of unique value for 'classifierKey'
			// from the subset of data satisfying classifier key/values given by 'this.keyValuesForDynamic'

			// get all unique combinations of values given by 'this.keyValuesForDynamic.keys' + 'classifierKey' keys
			var allKeys = this.keyValuesForDynamic.keys.concat(classifierKey);
			var combinations = this.slicerDataAccessAdapter.getUniqueCombinations(allKeys);

			var targetValues = this.keyValuesForDynamic.values[0];

			var valueSet = {};
			for (var rowIndex = 0; rowIndex < combinations.length; rowIndex++) {
				var row = combinations[rowIndex];

				// filter out combinations which are not equal to 'this.keyValuesForDynamic.values'
				var good = true;
				for (var i = 0; good && i < targetValues.length; i++) {
					good = (row[i] == targetValues[i]);
				}

				if (good) {
					valueSet[_.last(row)] = true;
				}
			}
			return _.size(valueSet);
		}
	});

	/**
	 * summary: evaluate the expression in scope, but replace TOTAL first
	 * @param {String} expr - expression to evaluate, examples: 
	 * 		TargetNoOfUnits*TargetRetail
	 * 		(TargetNoOfUnits_TargetRetail) != 0 ? (TargetCost * TargetNoOfUnits)/(TargetNoOfUnits_TargetRetail) : 0.0
	 * 		__Count__ / TOTAL(__Count__)
	 * 		COUNT_UNIQUE(Style)
	 * @param {Object} scope - object with expression variables values and with two special functions, defined above in expressionScope()
	 * 		{name:value, name2:value2...TOTAL:function, COUNT_UNIQUE:function} 
	 * @returns 
	 */
	var evalInScope = function(expr, scope) {
		// we now use mathjs.evaluate function
		// mathjs web site and documentation: https://mathjs.org/
		// mathjs gitHub https://github.com/josdejong/mathjs
		expr = expr.replace(/(TOTAL|COUNT_UNIQUE)\((.+?)\)/g, '$1("$2")');
		const result = evaluate(expr, scope);
		return result;
	};

	var KeyValues = declare(null, {
		// String[]: array of keys
		keys: null,

		// Object[][]: matrix of values
		//	each row represents a new value vector for corresponding key from 'keys' array
		values: null,

		constructor: function(keys, values) {
			this.keys = keys;
			this.values = values;
		},

		isEmpty: function() {
			return this.keys == null || this.keys.length == 0;
		},

		getKeyValuesForRow: function(/*Integer*/rowIndex) {
			// summary: create a new KeyValues object with same keys and values from the 'rowIndex' row
			return new KeyValues(this.keys, [this.values[rowIndex]]);
		},

		getCombinedKeyValues: function(/*KeyValues*/other) {
			// summary: combine keys and values of this and 'other' object into a new one
			// assume keys are different in 'this' and 'other' instances
			if (!other || other.isEmpty())
				return this;
			else if (this.isEmpty())
				return other;

			// make sure the number of rows are the same
			if (this.values.length != other.values.length)
				throw new Error("Length of this.values (" + this.values.length + ") must be equal to the length of other.values (" + other.values.length + ")");

			var keys = this.keys.concat(other.keys);
			var values = new Array(this.values.length);
			for (var i = 0; i < values.length; i++) {
				values[i] = this.values[i].concat(other.values[i]);
			}
			return new KeyValues(keys, values);
		}
	});

	var SeriesData = declare(SlicerDataAccess.SeriesData, {
		name: null,
		data: null,

		constructor: function(/*SlicerDataAccess*/slicerDataAccess, /*KeyValues*/graphClassifierValues, /*KeyValues*/compareValue, /*KeyValues*/rowValues, /*Integer*/rowTotalIndex, /*String*/metricKey) {
			var comb = graphClassifierValues.getCombinedKeyValues(compareValue);

			var item = slicerDataAccess.getMetricStoreItem(metricKey);
			var ren = csiHeading.getHeadingRenderer(null, item.heading);

			// hack to scale percentage correctly
			var factor = (item.heading.m_format && item.heading.m_format.indexOf('%') >= 0) ? 100 : 1;

			if (compareValue) {
				this.name = _.map(compareValue.values[0], function(val) {
					return slicerDataAccess.getClassifierValueName(val);
				}, this).join();
			} else {
				this.name = "";
			}

			this.data = [];

			// keep totals of all metrics to be able to evaluate a dynamic function
			var totals = {};
			var value, displayValue;
			for (var i = 0; i < rowValues.values.length; i++) {
				var row = comb.getCombinedKeyValues(rowValues.getKeyValuesForRow(i));

				var values = slicerDataAccess.getMetricAggregationValues(row, comb);

				for (var key in values) {
					totals[key] = (totals[key] || 0) + (values[key] || 0);
				}

				value = values[metricKey];
				displayValue = null;
				if (value != null) {
					displayValue = ren.formatValue(value, item.heading.m_format);
					value *= factor;
				}

				this.data.push({value: value, displayvalue: displayValue});
			}

			// to get a total for dynamic metric, calculate the function instead of getting a sum
			var total;
			if (item && item.dynamic) {
				var scope = new DynamicScope(slicerDataAccess, totals, comb);
				total = evalInScope(item.func, expressionScope(scope, totals));
			} else {
				total = totals[metricKey];
			}

			if (rowTotalIndex >= 0) {
				displayValue = null;
				if (total != null) {
					displayValue = ren.formatValue(total, item.heading.m_format);
					total *= factor;
				}
				this.data.unshift({value: total, displayvalue: displayValue});
			}
		},

		getName: function() {
			// summary: return a name for the series
			return this.name;
		},

		getData: function() {
			// summary: return an array of objects in the format:
			// {
			//	value: <value>,
			//  displayvalue: <optional display value>
			// }
			return this.data;
		}
	});

	var GraphData = declare(SlicerDataAccess.GraphData, {
		slicerDataAccess: null,
		graphClassifierValues: null,
		compareValues: null,
		metricKey: null,
		metricFunc: null,

		// KeyValues:
		_xvalues: null,

		// Integer: the index in 'rowKeys' array of the classifier we are totalling on
		_rowTotalIndex: null,

		constructor: function(/*SlicerDataAccess*/slicerDataAccess, /*String*/rowTotalKey, /*KeyValues*/graphClassifierValues, /*KeyValues*/compareValues, /*String*/metricKey) {
			this.slicerDataAccess = slicerDataAccess;
			this.graphClassifierValues = graphClassifierValues;
			this.compareValues = compareValues;
			this.metricKey = metricKey;

			var rowKeys = this._getRowKeys();
			this._xvalues = this.slicerDataAccess.getKeyValuesUniqueCombinations(rowKeys);
			this._rowTotalIndex = _.indexOf(rowKeys, rowTotalKey);
		},

		getName: function() {
			// summary: return a name for the graph
			var value = this.graphClassifierValues.values[0];
			if (!value)
				return locale.resource("Total");

			var translatedValues = _.map(value, function(val, index) {
				var key = this.graphClassifierValues.keys[index];
				return this.slicerDataAccess.getClassifierValueName(val, key);
			}, this);

			return translatedValues.join("{br}");
		},

		getXAxisName: function() {
			// summary: return a name of X axis data
			var store = this.slicerDataAccess.getClassifierStore();

			var getName = function(key) {
				var item = store.get(key);
				return item ? item.name : key;
			}

			var rowKeys = this._getRowKeys();
			if (this._rowTotalIndex >= 0) {
				result = getName(rowKeys[this._rowTotalIndex]);
			} else {
				result = _.map(rowKeys, getName).join();
			}
			return result;
		},

		getYAxisName: function() {
			// summary: return a name of Y axis data
			var item = this.slicerDataAccess.getMetricStoreItem(this.metricKey);
			return item ? item.name : this.metricKey;
		},

		getXLabels: function() {
			// summary: return an array of labels for X axis
			var labels = [];

			if (this._rowTotalIndex >= 0)
				labels.push(locale.resource("Total"));

			var keys = this._xvalues.keys;
			_.forEach(this._xvalues.values, function(arrVal) {
				if (this._rowTotalIndex >= 0) {
					var val = arrVal[this._rowTotalIndex];
					labels.push(this.slicerDataAccess.getClassifierValueName(val, keys[this._rowTotalIndex]));
				} else {
					arrVal = _.map(arrVal, function(val, idx) {
						return this.slicerDataAccess.getClassifierValueName(val, keys[idx]);
					}, this);
					labels.push(arrVal.join());
				}
			}, this);

			return labels;
		},

		getSeries: function() {
			// summary: return an array of SeriesData
			var result = [];
			if (this.compareValues) {
				for (var i = 0; i < this.compareValues.values.length; i++) {
					var series = new SeriesData(this.slicerDataAccess, this.graphClassifierValues, this.compareValues.getKeyValuesForRow(i), this._xvalues, this._rowTotalIndex, this.metricKey);
					result.push(series);
				}
			} else {
				var series = new SeriesData(this.slicerDataAccess, this.graphClassifierValues, null, this._xvalues, this._rowTotalIndex, this.metricKey);
				result.push(series);
			}
			return result;
		},

		_getRowKeys: function() {
			return this.slicerDataAccess.slicerWidget.rowKeys;
		}
	});

	return declare(SlicerDataAccess, {
		slicerWidget: null,

		_keysToUniqueCombinationsCache: null,
		_keysToMetricValuesCache: null,
		_keysToMetricAggregationValuesCache: null,

		constructor: function(slicerWidget) {
			this.slicerWidget = slicerWidget;

			this._keysToUniqueCombinationsCache = {};
			this._keysToMetricValuesCache = {};
			this._keysToMetricAggregationValuesCache = {};
		},

		getData: function() {
			return this.slicerWidget._filteredData;
		},

		getDataStore: function() {
			return this.slicerWidget.store;
		},

		getClassifierStore: function() {
			return this.slicerWidget.classifierStore;
		},

		getMetricStore: function() {
			return this.slicerWidget.metricStore;
		},

		getGraphs: function(/*String*/metricKey) {
			// summary: return an array of GraphData for the given 'metricKey'
			var widget = this.slicerWidget;

			// get unique values of column specified in 'columnCompareKey'
			var compareValues = widget.columnCompareKey ? this.getKeyValuesUniqueCombinations([widget.columnCompareKey]) : null;

			// get unique combinations of values of columns specified in 'columnKeys' (with 'columnCompareKey' removed)
			var columnValues = this.getKeyValuesUniqueCombinations(_.without(widget.columnKeys, widget.columnCompareKey));

			// for the each column value combination create a graph
			var result = [];

			for (var i = 0; i < columnValues.values.length; i++) {
				var keyValues = columnValues.getKeyValuesForRow(i);
				result.push(new GraphData(this, widget.rowTotalKey, keyValues, compareValues, metricKey));
			}

			// add 'Total' graph if configured
			if (widget.columnTotalKey) {
				var keyValues = new KeyValues([], []);
				result.push(new GraphData(this, widget.rowTotalKey, keyValues, compareValues, metricKey));
			}

			return result;
		},

		getClassifierValueName: function(/*String*/classifierValue, /*String*/key) {
			if (key) {
				var spec = this.slicerWidget.getSpec(key);
				if (spec)
					return spec.ren.formatValue(classifierValue, spec.format);
			}
			return this.slicerWidget.getClassifierValueName(classifierValue, classifierValue);
		},

		getKeyValuesUniqueCombinations: function(/*String[]*/keys) {
			// summary:
			return new KeyValues(keys, this.getUniqueCombinations(keys));
		},

		getMetricStoreItem: function(/*String*/metricKey) {
			// summary: return the item from a metric store
			return this.getMetricStore().get(metricKey);
		},

		getMetricValues: function(/*KeyValues*/keyValues) {
			// summary: return a hash of MetricValues object representing metric values for the given 'keyValues'
			if (keyValues.values.length != 1)
				throw new Error("getMetricValues: the length of keyValues.values must be 1")

			var keysCombinationKey = keyValues.values[0].join(KEY_SEPARATOR);

			var result = this._keysToMetricValuesCache[keysCombinationKey];
			if (result) {
				return result;
			}

			// map keys to indices
			var dataStore = this.getDataStore();
			var indices = _.map(keyValues.keys, dataStore.getKeyIndex, dataStore);

			// get all values for all metricKeys
			var metricValues = this.getMetricStore().query({}).map(function(item) {
				return new MetricValues(item);
			}, this);

			var data = this.getData();
			for (var rowIndex = 0; rowIndex < data.length; rowIndex++) {
				var row = data[rowIndex];

				var good = true;
				for (var i = 0; good && i < indices.length; i++) {
					var dataValue = row[indices[i]];
					var targetValue = keyValues.values[0][i];
					good = (dataValue == targetValue);
				}

				if (good) {
					for (var i = 0; i < metricValues.length; i++) {
						var key = metricValues[i].item.id;
						var index = dataStore.getKeyIndex(key);
						metricValues[i].values.push(row[index]);
					}
				}
			}

			// make a hash
			result = {};
			_.forEach(metricValues, function(mv) {
				result[mv.item.id] = mv;
			});

			// cache result
			this._keysToMetricValuesCache[keysCombinationKey] = result;

			return result;
		},

		getMetricAggregationValues: function(/*KeyValues*/keyValues, /*KeyValues*/keyValuesForDynamic) {
			// summary: return a hash of all calculated aggregation functions of metric values for the given 'keyValues'
			if (keyValues.values.length != 1)
				throw new Error("getMetricAggregationValues: the length of keyValues.values must be 1")

			var keysCombinationKey = keyValues.values[0].join(KEY_SEPARATOR);

			var result = this._keysToMetricAggregationValuesCache[keysCombinationKey];
			if (result) {
				return result;
			}

			var metricValues = this.getMetricValues(keyValues);

			result = {};

			// apply aggregation functions for all non-dynamic metric
			_.forEach(metricValues, function(mv, idx, obj) {
				const item = mv.item;
				if (item.dynamic)
					return;

				const func = FUNCTIONS[item.func];
				if (!func)
					throw new Error("Non-dynamic function '" + item.func + "' is not supported in charts");

				const mvValues = mv.values;
				if (item.func == "WEIGHT_AVG") {
					const weightName = mv.item.weightAttributeName;
					const weightValues = obj[weightName].values;
					result[item.id] = ((mvValues.length > 0) && (weightValues.length > 0)) ? func(mvValues, weightValues) : null;
				} else {
					result[item.id] = (mvValues.length > 0) ? func(mvValues) : null;
				}
			}, this);

			// for dynamic metric evaluate a function expression in the scope of already calculated results
			var scope = new DynamicScope(this, result, keyValuesForDynamic);
			_.forEach(metricValues, function(mv) {
				var item = mv.item;
				if (!item.dynamic)
					return;

				try {
					result[item.id] = evalInScope(item.func, expressionScope(scope, result));
				} catch (ex) {
					result[item.id] = 0;
				}
			}, this);

			// cache result
			this._keysToMetricAggregationValuesCache[keysCombinationKey] = result;

			return result;
		},

		getUniqueCombinations: function(/*String[]*/keys) {
			// summary: get unique combination of values from the combination of keys
			var keysCombinationKey = keys.join(KEY_SEPARATOR);
			var result = this._keysToUniqueCombinationsCache[keysCombinationKey];
			if (result !== undefined) {
				return result;
			}

			var store = this.slicerWidget._getClassifierValueStore(false, keys);
			var result = [];
			store.query().forEach(function(item) {
				var combinedValue = new Array(keys.length);
				for (var i = 0; i < keys.length; i++) {
					combinedValue[i] = item[keys[i]];
				}
				result.push(combinedValue);
			});

			// cache result
			this._keysToUniqueCombinationsCache[keysCombinationKey] = result;

			return result;
		}
	});
});